import { EMPTY, Observable, of, OperatorFunction } from 'rxjs';
import { catchError, ignoreElements } from 'rxjs/operators';

// throw an error that would be picked up globally as an unhandled error,
// without having to emit an error on the observable (which would close the stream).
// There could be unintended issues with doing it this way, but it seems fine for now i guess.
// This is basically like https://github.com/ReactiveX/rxjs/blob/7.8.2/src/internal/util/reportUnhandledError.ts in rxjs
export function throwAsyncError(error: unknown): void {
  setTimeout(() => {
    throw error;
  });
}

// usable at the end of an expression:
// return somecall() || throwSyncError();
// OR
// return somecall() ?? throwSyncError();
export function throwSyncError(error: unknown): never {
  throw error;
}

// rxjs operator to not emit the error, and rethrow error globally.
// Usage: Don't let a possible load error get emitted down the stream,
// which would cause it to be closed for subscribers.
// To actually keep an outer stream from closing, this needs to be inside an inner stream via a mapping operator,
// like switchMap, concatMap, mergeMap, exhaustMap, etc.
// IMPORTANT: there amy be many cases where we want to use reportErrorToUserAndServerInstance instead of this.
export function ignoreErrorAndRethrow<T>(onError?: (err: unknown) => void): OperatorFunction<T, T> {
  return catchError((e: unknown) => {
    throwAsyncError(e);
    onError?.(e);
    return EMPTY;
  });
}

// similar to ignoreErrorAndRethrow, but you can return an observable from onError
export function ignoreErrorAndRethrow$<T, T2>(onError: (err: unknown) => Observable<T2>): OperatorFunction<T, T> {
  return catchError((e: unknown) => {
    throwAsyncError(e);
    return onError(e).pipe(
      // handle new errors from onError
      ignoreErrorAndRethrow(),
      // don't emit any values from it
      ignoreElements(),
    );
  });
}

// like ignoreErrorAndRethrow, but return a single default value
export function defaultAndRethrow<T, T2>(onError: (err: unknown) => T2): OperatorFunction<T, T | T2> {
  return catchError((e: unknown): Observable<T2> => {
    throwAsyncError(e);
    return of(onError(e));
  });
}
